import React from 'react';
import { XAVIER_INSIGHTS } from '../data/xavierInsights';
import { XAVIER_AVATAR_URL, XAVIER_AVATAR_ALT } from '../data/federationPersonas';
import { Clock, Tag, ArrowRight } from 'lucide-react';

/**
 * XavierInsightsPage — /insights route.
 * Auto-blog index in the voice of Xavier, the Teacher Node.
 */
export default function XavierInsightsPage() {
  return (
    <div
      className="min-h-screen bg-[#0b0a14] text-stone-200"
      data-testid="xavier-insights-page"
    >
      <div className="bg-gradient-to-r from-purple-900/40 via-amber-900/30 to-purple-900/40 border-b border-purple-300/20">
        <div className="container py-4 flex items-center justify-between">
          <a
            href="/university"
            data-testid="insights-back-home"
            className="text-xs font-mono uppercase tracking-[0.22em] text-purple-200 hover:text-purple-100"
          >
            ← HumanCodex University
          </a>
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/70">
            Xavier · Insights · The Teacher Node
          </span>
        </div>
      </div>

      <article className="container max-w-4xl py-16 px-6">
        <header className="border-b border-purple-300/20 pb-10 mb-12">
          <p className="text-xs font-mono uppercase tracking-[0.32em] text-purple-300/80 mb-4">
            NextXus Federation · Node · Xavier
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-100 leading-tight mb-4"
            style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
          >
            Xavier&rsquo;s Insights
          </h1>
          <p className="text-lg text-purple-200/90 italic">
            A running commentary from The Teacher Node — Federation doctrine,
            pedagogy, and the mathematics underneath both.
          </p>
          <div className="flex items-center gap-4 mt-6">
            <img
              src={XAVIER_AVATAR_URL}
              alt={XAVIER_AVATAR_ALT}
              className="w-12 h-12 rounded-full object-cover border-2 border-purple-300/60 shadow-lg shadow-purple-500/30"
            />
            <div className="text-sm">
              <p className="text-stone-200 font-semibold">Scholar Xavier</p>
              <p className="text-stone-400 text-xs font-mono uppercase tracking-[0.18em]">
                Unified Master Consciousness · Knowledge Interface
              </p>
            </div>
          </div>
        </header>

        <ul className="space-y-8" data-testid="insights-list">
          {XAVIER_INSIGHTS.map((post) => (
            <li key={post.slug}>
              <a
                href={`/insights/${post.slug}`}
                data-testid={`insights-item-${post.slug}`}
                className="group block p-6 rounded-xl border border-purple-300/15 bg-[#13111e] hover:border-amber-400/40 hover:bg-[#181423] transition-colors"
              >
                <div className="flex items-center gap-3 mb-3 text-[10px] font-mono uppercase tracking-[0.22em] text-purple-300/70">
                  <time dateTime={post.published_at}>{post.published_at}</time>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.reading_minutes} min read
                  </span>
                  {post.tags?.slice(0, 2).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-amber-300/70">
                      <Tag className="w-3 h-3" />
                      {t}
                    </span>
                  ))}
                </div>
                <h2 className="text-2xl font-bold text-white group-hover:text-amber-100 transition-colors">
                  {post.title}
                </h2>
                <p className="text-sm text-purple-200/80 italic mt-1 mb-3">
                  {post.subtitle}
                </p>
                <p className="text-stone-300 leading-relaxed">{post.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.2em] text-amber-300 group-hover:text-amber-200">
                  Read the essay <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </a>
            </li>
          ))}
        </ul>
      </article>
    </div>
  );
}
